import { CALLOUT_REF_WIDTH } from "../shared/callout";

const tracked = new WeakSet<HTMLElement>();

/**
 * A callout card is designed for a screen CALLOUT_REF_WIDTH px wide (shared/callout.ts): its layer (`.callout-ref`) is
 * laid out as that screen and scaled to the screen it is drawn over, its parent. So the card's width, text, padding and
 * arrow, its %-position and the CSS that keeps it on screen come out as the same part of the page in every view: the
 * Studio at any zoom, Quick Preview, Preview and the published view, at any window size.
 *
 * Publishes the scale as `--k` on the layer (and its inverse as `--ik`, for editor chrome that stays its own size), and
 * keeps it current as the screen is resized. A ResizeObserver reports before the browser paints, so the card is never
 * drawn at the wrong size. Used as a callback ref, so it is safe to call on every render.
 */
export function scaleCalloutLayer(el: HTMLElement | null) {
  if (!el || tracked.has(el) || !el.parentElement) return;
  tracked.add(el);
  const screen = el.parentElement;
  const set = (w: number) => {
    if (!(w > 0)) return;
    el.style.setProperty("--k", String(w / CALLOUT_REF_WIDTH));
    el.style.setProperty("--ik", String(CALLOUT_REF_WIDTH / w));
  };
  set(screen.offsetWidth);
  if (typeof ResizeObserver === "undefined") return;
  const ro = new ResizeObserver((entries) => {
    if (!el.isConnected) { ro.disconnect(); return; }
    set(entries[entries.length - 1].contentRect.width);
  });
  ro.observe(screen);
}

/** A callout layer's own style: as tall as a CALLOUT_REF_WIDTH-wide screen of this shape, shifted by (dx, dy) % of it. */
export function calloutLayerStyle(imageWidth: number, imageHeight: number, dx = 0, dy = 0): React.CSSProperties {
  return {
    height: `${(CALLOUT_REF_WIDTH * imageHeight) / Math.max(1, imageWidth)}px`,
    ["--dx" as any]: `${dx}%`,
    ["--dy" as any]: `${dy}%`,
  } as React.CSSProperties;
}
